/**
 * MeasureCard Component
 *
 * Expandable card for displaying a single clinical quality measure.
 * Shows measure name, status badge, and expandable detail section.
 * For N/A measures, shows eligibility criteria checklist with pass/fail indicators.
 * Uses sidecar-style visual patterns:
 * - Priority border colors (red unmet, green met, gray N/A)
 * - Pulse sweep effect for unmet+eligible measures
 * - Measure status icons
 * - AnimatePresence expand/collapse transitions
 */

import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, AlertCircle, CheckCircle2, Circle, XCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import React from 'react'
import type { PatientMeasureItem, ClinicalValue, EligibilityCriteria } from '@/lib/equip-types'
import { getMeasureDisplayName } from '@/lib/equip-utils'

interface MeasureCardProps {
  measure: PatientMeasureItem
  isExpanded: boolean
  onToggle: () => void
}

// Color mapping for clinical value status
const statusColors: Record<string, string> = {
  normal: 'text-green-700 dark:text-green-400',
  abnormal: 'text-orange-600 dark:text-orange-400',
  critical: 'text-red-600 dark:text-red-400',
}

const statusBgColors: Record<string, string> = {
  normal: 'bg-green-50 border-green-200 dark:bg-green-950/40 dark:border-green-800',
  abnormal: 'bg-orange-50 border-orange-200 dark:bg-orange-950/40 dark:border-orange-800',
  critical: 'bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-800',
}

function ClinicalValueCard({ cv }: { cv: ClinicalValue }) {
  const colorClass = cv.status ? statusColors[cv.status] : 'text-foreground'
  const bgClass = cv.status ? statusBgColors[cv.status] : 'bg-muted/50 border-border'

  return (
    <div className={cn('rounded-lg border px-3 py-2', bgClass)}>
      <div className="text-xs text-muted-foreground">{cv.label}</div>
      <div className={cn('text-sm font-medium', colorClass)}>{cv.value}</div>
    </div>
  )
}

function EligibilityCriteriaChecklist({ criteria }: { criteria: EligibilityCriteria }) {
  const hasInclusion = criteria.inclusionRequirements.length > 0
  const hasExclusion = criteria.exclusionCriteria.length > 0

  if (!hasInclusion && !hasExclusion) return null

  return (
    <div className="space-y-3">
      {hasInclusion && (
        <div>
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
            Inclusion Requirements
          </div>
          <div className="space-y-1">
            {criteria.inclusionRequirements.map((req) => (
              <div key={req.description} className="flex items-start gap-2">
                {req.met ? (
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                ) : (
                  <Circle className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" />
                )}
                <span className={cn('text-sm', req.met ? 'text-foreground' : 'text-muted-foreground')}>
                  {req.description}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasExclusion && (
        <div>
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
            Exclusion Criteria
          </div>
          <div className="space-y-1">
            {criteria.exclusionCriteria.map((exc) => (
              <div key={exc.description} className="flex items-start gap-2">
                {exc.met ? (
                  <XCircle className="h-4 w-4 text-red-500 dark:text-red-400 shrink-0 mt-0.5" />
                ) : (
                  <Circle className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" />
                )}
                <span className={cn('text-sm', exc.met ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground')}>
                  {exc.description}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function getCollapsedPreview(measure: PatientMeasureItem): string | null {
  if (measure.isEligible) {
    if (!measure.clinicalValues || measure.clinicalValues.length === 0) return null
    return measure.clinicalValues
      .map((cv) => `${cv.label}: ${cv.value}`)
      .join('   ')
  }
  // For N/A: show why
  const criteria = measure.eligibilityCriteria
  if (!criteria) return 'Not eligible'
  const unmetInclusion = criteria.inclusionRequirements.find((r) => !r.met)
  const triggeredExclusion = criteria.exclusionCriteria.find((r) => r.met)
  if (unmetInclusion) return unmetInclusion.description
  if (triggeredExclusion) return `Excluded: ${triggeredExclusion.description}`
  return 'Not eligible'
}

function MeasureCardInner({ measure, isExpanded, onToggle }: MeasureCardProps) {
  const collapsedPreview = getCollapsedPreview(measure)

  // Priority border styling based on measure status
  const getBorderStyle = (): string => {
    if (!measure.isEligible) return 'border-l-4 border-l-muted-foreground/30 border border-border'
    if (measure.isMet) return 'border-l-4 border-l-green-500 dark:border-l-green-400 border border-green-200 dark:border-green-800'
    return 'border-l-4 border-l-red-500 dark:border-l-red-400 border border-red-200 dark:border-red-800'
  }

  // Status icon based on measure status
  const getMeasureIcon = () => {
    if (!measure.isEligible) return <Circle className="h-4 w-4 text-muted-foreground" />
    if (measure.isMet) return <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
    return <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
  }

  // Enhanced badge with pulse effect for unmet measures
  const renderBadge = () => {
    if (!measure.isEligible) {
      const hasTriggeredExclusion = measure.eligibilityCriteria?.exclusionCriteria.some((e) => e.met)
      return <Badge variant="muted">{hasTriggeredExclusion ? 'Excluded' : 'Not Eligible'}</Badge>
    }
    if (measure.isMet) return <Badge variant="lightSuccess">Met</Badge>

    return (
      <motion.div
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ repeat: Infinity, duration: 2 }}
      >
        <Badge variant="lightError">
          <AlertCircle className="w-3 h-3 mr-1" />
          Unmet
        </Badge>
      </motion.div>
    )
  }

  return (
    <div
      className={cn(
        'relative rounded-lg bg-card shadow-sm cursor-pointer transition-shadow hover:shadow-md p-3',
        getBorderStyle()
      )}
      onClick={onToggle}
    >
      {/* Pulse sweep effect for unmet+eligible measures */}
      {!measure.isMet && measure.isEligible && (
        <motion.div
          className="absolute inset-0 pointer-events-none rounded-lg overflow-hidden"
          aria-hidden="true"
        >
          <motion.div
            className="absolute inset-0"
            style={{
              backgroundImage: 'linear-gradient(to right, transparent, hsl(var(--destructive) / 0.15), transparent)',
            }}
            animate={{ x: ['-100%', '100%'] }}
            transition={{ repeat: Infinity, duration: 3, ease: 'linear' }}
          />
        </motion.div>
      )}

      {/* Card header - always visible */}
      <div className="relative flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {getMeasureIcon()}
          <span className="text-sm font-medium">{getMeasureDisplayName(measure.measureName)}</span>
          {renderBadge()}
          {/* Collapsed preview - one-line summary */}
          {!isExpanded && collapsedPreview && (
            <span className="text-xs text-muted-foreground truncate hidden sm:inline">
              {collapsedPreview}
            </span>
          )}
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-200 shrink-0',
            isExpanded && 'rotate-180'
          )}
        />
      </div>

      {/* Expandable detail section with AnimatePresence */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="detail"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="mt-3 pt-3 border-t text-sm space-y-3">
              {/* Clinical values (shown for all measures) */}
              {measure.clinicalValues && measure.clinicalValues.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {measure.clinicalValues.map((cv) => (
                    <ClinicalValueCard key={cv.label} cv={cv} />
                  ))}
                </div>
              )}

              {/* Eligibility criteria checklist */}
              {measure.eligibilityCriteria && (
                <EligibilityCriteriaChecklist criteria={measure.eligibilityCriteria} />
              )}

              {/* Fallback if nothing to show */}
              {(!measure.clinicalValues || measure.clinicalValues.length === 0) &&
                !measure.eligibilityCriteria && (
                <div className="text-muted-foreground italic">No clinical data available</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// Memoize component to prevent pulse animation re-triggering on parent re-renders
export const MeasureCard = React.memo(MeasureCardInner, (prev, next) => {
  return (
    prev.measure.measureName === next.measure.measureName &&
    prev.isExpanded === next.isExpanded &&
    prev.measure.isMet === next.measure.isMet &&
    prev.measure.isEligible === next.measure.isEligible &&
    prev.measure.clinicalValues === next.measure.clinicalValues &&
    prev.measure.eligibilityCriteria === next.measure.eligibilityCriteria
  )
})
